import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Combined effect to check auth status on load and handle OAuth redirect
  useEffect(() => {
    const validateToken = async (token) => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, { // Use the correct profile endpoint
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          if (userData && userData.id) { // Ensure we got valid user data
            setUser(userData);
            localStorage.setItem('authToken', token); // Ensure token is stored/refreshed
          } else {
             // Handle cases where profile endpoint returns ok but no valid user data
             console.warn('Profile endpoint returned ok, but no valid user data');
             localStorage.removeItem('authToken');
             setUser(null);
          }
        } else {
          // Token invalid or other error, clear localStorage
          console.log(`Token validation failed with status: ${response.status}`);
          localStorage.removeItem('authToken');
          setUser(null);
        }
      } catch (err) {
        console.error('Auth validation fetch error:', err);
        localStorage.removeItem('authToken'); // Clear token on fetch error too
        setUser(null);
        setError('Failed to validate session');
      } finally {
         // Set loading to false only after validation attempt is complete
         // Do NOT set loading false inside the try/catch if validation fails early
      }
    };

    const checkAuthStatus = async () => {
      setLoading(true); // Start loading
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        // If token is in URL (OAuth redirect)
        console.log('Found token in URL, validating...');
        window.history.replaceState({}, document.title, window.location.pathname); // Clean up URL
        await validateToken(tokenFromUrl);
      } else {
        // If no token in URL, check localStorage
        const tokenFromStorage = localStorage.getItem('authToken');
        if (tokenFromStorage) {
          console.log('Found token in localStorage, validating...');
          await validateToken(tokenFromStorage);
        } else {
           // No token found anywhere
           console.log('No token found in URL or localStorage.');
           setUser(null); // Explicitly set user to null
        }
      }
      setLoading(false); // Finish loading AFTER all checks
    };

    checkAuthStatus();
    // Dependency array is empty, so this runs only once on mount
  }, []); 

  // Login function
  const login = async (email, password) => {
    setError(null);
    setLoading(true); // Set loading during login
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      setLoading(false); // Finish loading on successful login
      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false); // Finish loading on error
      throw err;
    }
  };

  // Register function
  const register = async (name, email, password) => {
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store token and user data
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Logout function
  const logout = () => {
    console.log('Logging out, clearing token and user.');
    localStorage.removeItem('authToken');
    setUser(null);
    // Optional: Redirect to login or home page after logout
    // window.location.href = '/auth'; 
  };

  // Google OAuth login helper
  const handleGoogleLogin = () => {
    // Ensure API URL is correctly determined
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    window.location.href = `${apiUrl}/auth/google`;
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user,
        loading,
        error,
        login,
        register,
        logout,
        handleGoogleLogin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 